import {
  checkInputErrorMaxLength,
  useAnalyticsPageView,
  useEffectOnce,
  useInput,
  usePagination,
  usePathParam
} from '@chilecompra/react-kit'
import { Input, Pagination, Toast, Typography } from '@chilecompra/react-kit/components'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { SANCTIONS_ROWS_PER_PAGE, USER_TYPE } from '../../config/settings/constant'
import { PUBLIC_URL } from '../../config/settings/environment'
import useReCaptcha from '../../modules/hooks/reCaptcha'
import { formatOnlyLettersNumbersAndSomeSymbols } from '../../modules/utils/formatters'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { SanctionRow, SanctionsTable, TableSkeleton } from './components'
import { SanctionVoidDialog } from './components/SanctionVoidDialog'
import {
  onCloseSanctionVoidDialog,
  onGetHistoricalSanctionsListThunk,
  onGetSanctionVoidInfoThunk,
  onOpenSanctionVoidDialog
} from './HistoricalSanctionsList.actions'
import { Card, Container, Item, NotFoundImg, SearchIcon, SearchLoadingIcon } from './HistoricalSanctionsList.styles'

/**
 * The HistoricalSanctionsList container.
 */
const HistoricalSanctionsList = () => {
  const { userType } = useAuthorization()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { executeReCaptcha } = useReCaptcha()

  const {
    data,
    error,
    keyword: appliedKeyword,
    loading,
    page: currentPage,
    sanctionVoidInfo,
    searching,
    showSanctionVoidDialog,
    totalPages,
    totalRecords
  } = useSelector(state => state.historicalSanctionsList)

  const {
    error: keywordError,
    onChange: handleKeywordChange,
    value: keywordValue
  } = useInput({
    errorCallbacks: [checkInputErrorMaxLength(200)],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols],
    overwriteCallback: () => appliedKeyword
  })

  const { onChangePage, page, records, setPage } = usePagination({
    data,
    initialPage: 1,
    initialRowsPerPage: SANCTIONS_ROWS_PER_PAGE
  })

  const fetchSanctions = nextPage =>
    dispatch(onGetHistoricalSanctionsListThunk({ dni, keyword: keywordValue, page: nextPage, searching: true }))

  const fetchSanctionVoidInfo = useCallback(
    sanctionId => dispatch(onGetSanctionVoidInfoThunk({ sanctionId })),
    [dispatch]
  )

  const handleChangePage = (event, nextPage) => {
    if (nextPage === currentPage) return

    onChangePage(event, nextPage)
    fetchSanctions(nextPage)
  }

  const handleCloseSanctionVoidDialog = useCallback(() => dispatch(onCloseSanctionVoidDialog()), [dispatch])

  const handleOpenSanctionVoidDialog = useCallback(() => dispatch(onOpenSanctionVoidDialog()), [dispatch])

  const handleSearchSanctions = () => {
    if (keywordError !== '' || searching) return

    setPage(1)
    fetchSanctions(1)
  }

  useEffectOnce(() => {
    const fetchList = async () => {
      if (userType === USER_TYPE.PUBLIC) await executeReCaptcha({ action: 'publicInfo' })

      dispatch(onGetHistoricalSanctionsListThunk({ dni, keyword: '', loading: true }))
    }

    fetchList()
  })

  useAnalyticsPageView('/ficha/sancion/historico')

  const isReady = !loading && !error
  const isEmpty = isReady && data.length === 0
  const isInitialEmpty = isEmpty && appliedKeyword.length === 0
  const showNoResults = isEmpty && appliedKeyword.length > 0
  const showTable = isReady && data.length > 0
  const showPagination = showTable && totalRecords > SANCTIONS_ROWS_PER_PAGE

  return (
    <Container gap="32px">
      <Container justifyContent="flex-end">
        <Item md={4} sm={6} xs={12}>
          <Input
            disabled={error || searching || isInitialEmpty}
            error={keywordError}
            loading={loading}
            onBlur={handleSearchSanctions}
            onChange={handleKeywordChange}
            onKeyPress={({ key }) => key === 'Enter' && handleSearchSanctions()}
            placeholder="Buscar por palabra clave..."
            suffix={
              searching ? (
                <SearchLoadingIcon />
              ) : (
                <SearchIcon disabled={keywordError !== ''} onClick={handleSearchSanctions} />
              )
            }
            value={keywordValue}
          />
        </Item>
      </Container>

      {loading && <TableSkeleton />}

      {!loading && error && (
        <Toast
          border
          severity="error"
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor."
          width="100%"
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}

      {isInitialEmpty && (
        <Toast border severity="info" width="100%">
          Este proveedor no registra sanciones históricas.
        </Toast>
      )}

      {showNoResults && (
        <Card className="not__found" color="blank" enableBorder padding="24px">
          <Container alignItems="center" direction="column" justifyContent="center">
            <NotFoundImg src={`${PUBLIC_URL}/assets/images/notFound.svg`} />

            <Typography fontWeight="bold" margin="16px 0" variant="h4">
              No se encontraron resultados para tu búsqueda.
            </Typography>

            <Typography color="black3" variant="body2">
              Intenta con otra palabra clave u otro criterio de búsqueda.
            </Typography>
          </Container>
        </Card>
      )}

      {showTable && (
        <SanctionsTable>
          {data.map((item, index) => (
            <SanctionRow
              disabled={searching}
              isLast={index === data.length - 1}
              item={item}
              key={`sanction-${item.id}`}
              onFetchSanctionVoidInfo={() => fetchSanctionVoidInfo(item.id)}
              onOpenSanctionVoidDialog={handleOpenSanctionVoidDialog}
            />
          ))}
        </SanctionsTable>
      )}

      {showPagination && (
        <Pagination
          count={totalPages}
          disabled={searching}
          disableFirstButton={false}
          disableLastButton={false}
          onChange={handleChangePage}
          page={page}
          records={records}
          size="small"
          total={totalRecords}
        />
      )}

      <SanctionVoidDialog
        onCloseSanctionVoidDialog={handleCloseSanctionVoidDialog}
        sanctionVoidInfo={sanctionVoidInfo}
        showSanctionVoidDialog={showSanctionVoidDialog}
      />
    </Container>
  )
}

export default HistoricalSanctionsList
